import React from 'react';
interface SkeletonProps {
  /** Width class (Tailwind) — default: 'w-full' */
  className?: string;
  /** Variant: 'text' (single line), 'circle', 'rect' (card-sized block) */
  variant?: 'text' | 'circle' | 'rect';
  /** Number of text lines to render (only for variant='text') */
  lines?: number;
}
/**
 * Skeleton loading placeholder — shows animated pulse blocks.
 * Use inside portal pages while data is loading.
 */
export function Skeleton({
  className = '',
  variant = 'text',
  lines = 1
}: SkeletonProps) {
  if (variant === 'circle') {
    return <div className={`rounded-full bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2417" />;
  }
  if (variant === 'rect') {
    return <div className={`rounded-2xl bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2418" />;
  }
  // Text variant — render multiple lines
  return <div className={`space-y-2.5 ${className}`} aria-hidden="true" data-id="element-2419">
      {Array.from({
      length: lines
    }).map((_, i) => <div key={i} className={`h-4 bg-slate-200 rounded-md animate-pulse ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`} data-id="element-2420" />)}
    </div>;
}
/**
 * Skeleton card — mimics a StatCard or summary card while loading.
 */
export function SkeletonCard({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-6 shadow-sm ${className}`} aria-hidden="true" data-id="element-2421">
      <div className="flex items-center gap-4" data-id="element-2422">
        <Skeleton variant="circle" className="w-12 h-12" data-id="element-2423" />
        <div className="flex-1 space-y-2" data-id="element-2424">
          <Skeleton className="w-24" data-id="element-2425" />
          <Skeleton className="w-16 h-6" data-id="element-2426" />
        </div>
      </div>
    </div>;
}
/**
 * Skeleton page — full page loading state for portal pages.
 * Shows 3 summary cards + a content block.
 */
export function SkeletonPage() {
  return <div className="max-w-7xl mx-auto p-6 space-y-8" aria-busy="true" aria-label="Laden..." data-id="element-2427">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2428">
        <SkeletonCard data-id="element-2429" />
        <SkeletonCard data-id="element-2430" />
        <SkeletonCard data-id="element-2431" />
      </div>

      {/* Content block */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-id="element-2432">
        <Skeleton className="w-48 h-5" data-id="element-2433" />
        <div className="space-y-3" data-id="element-2434">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-4" data-id="element-2435">
              <Skeleton variant="circle" className="w-10 h-10" data-id="element-2436" />
              <div className="flex-1" data-id="element-2437">
                <Skeleton className="w-32" data-id="element-2438" />
              </div>
              <Skeleton variant="rect" className="w-24 h-3" data-id="element-2439" />
              <Skeleton className="w-10 h-5" data-id="element-2440" />
            </div>)}
        </div>
      </div>
    </div>;
}